/* eslint-disable max-lines */
import React, {Component} from 'react';

import {autobind} from 'core-decorators';
import {Parser} from 'html-to-react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Button from 'Frontend/components/Button';
import {ModuleMargin} from 'Frontend/components/ModuleMargins';

import {parseToUri} from 'Admin/utils/formParsers';
import {Download as DownloadIcon} from 'Frontend/assets/images/icons';
import {FONTS} from 'Frontend/utils/fonts';
import {clampCss, iconAsBackground, pxToVw} from 'Frontend/utils/helpers';
import {MEDIA} from 'Frontend/utils/mediaQueries';
import {isValidNode, processingInstructions} from 'Frontend/utils/parserInstructions';

const POS = {
    center: 'center',
    left: 'flex-start',
    right: 'flex-end'
};

/**
 * CTA
 *
 * @property {*} prop Prop comment.
 *
 * @class CTA
 * @extends {Component}
 */
class CTA extends Component {
    static propTypes = {
        button: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        position: PropTypes.string.isRequired,
        hasBox: PropTypes.bool,
        hasSideContent: PropTypes.bool,
        isInGroup: PropTypes.bool,
        isQuickLead: PropTypes.bool,
        name: PropTypes.string,
        sideContent: PropTypes.shape({
            copy: PropTypes.string,
            downloadAsset: PropTypes.object,
            downloadLink: PropTypes.string,
            downloadLinkName: PropTypes.string,
            // eslint-disable-next-line react/sort-prop-types
            headline: PropTypes.string,
            isAsset: PropTypes.bool
        })
    }

    static defaultProps = {
        hasBox: false,
        hasSideContent: false,
        isInGroup: false,
        isQuickLead: false,
        name: null,
        sideContent: {}
    }

    htmlParser = new Parser();

    /**
     * Gets copy data from props.
     *
     * @returns {Object} The copy data.
     * @memberof InfoBox
     */
    getSideContentData() {
        const {sideContent} = this.props;

        if (sideContent) {
            const {copy, downloadAsset, downloadLink, downloadLinkName, headline, isAsset} = sideContent;

            return {
                copy,
                downloadAsset,
                downloadLink,
                downloadLinkName,
                headline,
                isAsset
            };
        }

        return {};
    }

    /**
     * Handle the survey tracking.
     *
     * @memberof Cta
     */
    @autobind
    handleTrack() {
        const {isQuickLead} = this.props;

        window.dataLayer.push({
            event: isQuickLead ? 'surveyShortInit' : 'surveyInit',
            PageTitle: document.title.innerText,
            URL: window.location.href
        });
    }

    /**
     * Renders the download component.
     *
     * @param {Object}  downloadAsset    Download asset object.
     * @param {String}  downloadLink     Download link.
     * @param {String}  downloadLinkName Download name.
     * @param {Boolean} isAsset          Flag if download is an asset.
     *
     * @returns {JSX} The download component.
     * @memberof InfoBox
     */
    renderDownload(downloadAsset, downloadLink, downloadLinkName, isAsset) {
        if (isAsset && downloadAsset) {
            const {ext, fileName, publicPath: {src}} = downloadAsset;

            return (
                <Download href={src} target="_blank" download>
                    {downloadLinkName || `${fileName}${ext}`}
                </Download>
            );
        } else if (downloadLink && downloadLinkName) {
            return (
                <Download href={downloadLink} target="_blank" download>
                    {downloadLinkName}
                </Download>
            );
        }

        return null;
    }

    /**
     * Renders the Component.
     *
     * @returns {JSX} Component.
     * @memberof CTA
     */
    render() {
        const {
            button,
            description,
            hasBox,
            hasSideContent,
            isInGroup,
            isQuickLead,
            name,
            position
        } = this.props;
        const {copy, downloadAsset, downloadLink, downloadLinkName, headline, isAsset} = this.getSideContentData();

        const link = isQuickLead ? '/interview-short' : '/interview';

        return (
            <CTAElement isInGroup={isInGroup} position={position}>
                {/* eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid */}
                <a id={parseToUri(name)} />
                <CTAContainer hasBox={hasBox} hasSideContent={hasSideContent} position={position}>
                    <CTAWrap hasBox={hasBox} hasSideContent={hasSideContent} position={position}>
                        <CTADesc hasBox={hasBox} hasSideContent={hasSideContent} position={position}>
                            {description}
                        </CTADesc>
                        <Button link={link} text={button} onClick={this.handleTrack} />
                    </CTAWrap>
                </CTAContainer>
                {hasSideContent && (
                    <SideContentContainer isInGroup={isInGroup} position={position}>
                        <Headline position={position}>
                            {headline}
                        </Headline>
                        {copy && (
                            <Text position={position}>
                                {this.htmlParser.parseWithInstructions(
                                    `<div data-module="CTA">${copy}</div>`,
                                    isValidNode,
                                    processingInstructions
                                )}
                            </Text>
                        )}
                        {(downloadLink || downloadAsset)
                            && this.renderDownload(downloadAsset, downloadLink, downloadLinkName, isAsset)}
                    </SideContentContainer>
                )}
            </CTAElement>
        );
    }
}

export default CTA;

const CTAElement = styled(ModuleMargin)`
    align-items: center;
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: center;
    margin-left: auto;
    margin-right: auto;
    max-width: 100%;
    position: relative;
    width: max(290px, ${pxToVw(327, 'm')});

    ${MEDIA.TABLET.UP} {
        flex-direction: ${({position}) => (position === 'left' ? 'row' : 'row-reverse')};
        justify-content: ${({hasBox, hasSideContent, position}) => ((hasBox || hasSideContent || position === 'center') ? 'center' : 'flex-start')};
    }

    ${MEDIA.DESKTOP.UP} {
        max-width: 100%;
        padding: ${({isInGroup}) => (isInGroup ? `0 max(110px, ${pxToVw(110, 'd')}) 0 max(110px, ${pxToVw(110, 'd')})` : '0')};
        width: max(1320px, ${pxToVw(1320, 'd')});
    }
`;

/* eslint-disable no-nested-ternary */
const CTAContainer = styled.div`
    border: ${({hasBox, hasSideContent}) => ((hasBox || hasSideContent) ? clampCss(24, 'm') : '0')} solid #FAEFED;
    display: flex;
    justify-content: center;
    margin-bottom: ${clampCss(40, 'm')};
    padding: ${({hasBox, hasSideContent}) => ((hasBox || hasSideContent) ? `${clampCss(64, 'm')} ${clampCss(23, 'm')} ${clampCss(80, 'm')} ${clampCss(23, 'm')}` : '0')};
    width: 100%;

    ${MEDIA.TABLET.UP} {
        border: ${({hasBox, hasSideContent}) => ((hasBox || hasSideContent) ? clampCss(24, 't') : '0')} solid #FAEFED;
        justify-content: ${({hasBox, hasSideContent, position}) => ((hasBox || hasSideContent || position === 'center') ? 'center' : 'flex-start')};
        margin-bottom: 0;
        margin-left: ${({hasSideContent, position}) => ((hasSideContent && position === 'right') ? clampCss(70, 't') : '0')};
        margin-right: ${({hasSideContent, position}) => ((hasSideContent && (position === 'left' || position === 'center')) ? clampCss(70, 't') : '0')};
        padding: ${({hasBox, hasSideContent}) => ((hasBox || hasSideContent) ? `${clampCss(44, 't')} ${clampCss(31, 't')} ${clampCss(44, 't')} ${clampCss(31, 't')}` : '0')};
        width: ${({hasBox, hasSideContent}) => (hasSideContent ? `calc(50% - ${clampCss(45, 't')})` : hasBox ? '100%' : '33.33%')};
    }

    ${MEDIA.DESKTOP.UP} {
        border: ${({hasBox, hasSideContent}) => ((hasBox || hasSideContent) ? `max(48px, ${pxToVw(48, 'd')})` : '0')} solid #FAEFED;
        margin-left: ${({hasSideContent, position}) => ((hasSideContent && position === 'right') ? `max(140px, ${pxToVw(140, 'd')})` : '0')};
        margin-right: ${({hasSideContent, position}) => ((hasSideContent && (position === 'left' || position === 'center')) ? `max(140px, ${pxToVw(140, 'd')})` : '0')};
        padding: ${({hasBox, hasSideContent}) => ((hasBox || hasSideContent) ? `max(88px, ${pxToVw(88, 'd')}) max(62px, ${pxToVw(62, 'd')}) max(88px, ${pxToVw(88, 'd')}) max(62px, ${pxToVw(62, 'd')})` : '0')};
        width: ${({hasBox, hasSideContent}) => (hasSideContent ? `calc(50% - max(70px, ${pxToVw(70, 'd')}))` : hasBox ? '100%' : '33.33%')};
    }
`;
/* eslint-enable no-nested-ternary */

const CTAWrap = styled.div`
    align-items: ${({hasBox, hasSideContent, position}) => ((hasBox || hasSideContent || position === 'center') ? 'center' : POS[String(position)])};
    display: flex;
    flex-direction: column;
    width: 100%;

    ${MEDIA.TABLET.UP} {
        align-items: ${({hasBox, hasSideContent, position}) => ((hasBox || hasSideContent || position === 'center') ? 'center' : 'flex-start')};
    }
`;

const CTADesc = styled.p`
    ${FONTS.SEMIBOLD}
    color: ${({theme}) => theme.colors.textColor};
    display: inline-block;
    font-size: ${clampCss(16, 'm')};
    letter-spacing: ${clampCss(1.2, 'm')};
    line-height: ${clampCss(22, 'm')};
    margin-bottom: ${clampCss(40, 'm')};
    text-align: ${({hasBox, hasSideContent, position}) => ((hasBox || hasSideContent || position === 'center') ? 'center' : position)};

    ${MEDIA.TABLET.UP} {
        font-size: ${clampCss(16, 't')};
        letter-spacing: ${clampCss(1.2, 't')};
        line-height: ${clampCss(22, 't')};
        margin-bottom: ${clampCss(32, 't')};
        text-align: ${({hasBox, hasSideContent, position}) => ((hasBox || hasSideContent || position === 'center') ? 'center' : 'left')};
    }

    ${MEDIA.DESKTOP.UP} {
        font-size: max(16px, ${pxToVw(16, 'd')});
        letter-spacing: max(1.2px, ${pxToVw(1.2, 'd')});
        line-height: max(22px, ${pxToVw(22, 'd')});
        margin-bottom: max(32px, ${pxToVw(32, 'd')});
    }
`;

const SideContentContainer = styled.div`
    width: 100%;

    ${MEDIA.TABLET.UP} {
        padding-left: ${({position}) => (position === 'right' ? clampCss(55, 't') : '0')};
        padding-right: ${({position}) => ((position === 'left' || position === 'center') ? clampCss(55, 't') : '0')};
        width: calc(50% - ${clampCss(45, 't')});
    }

    ${MEDIA.DESKTOP.UP} {
        padding-left: ${({position}) => (position === 'right' ? '110px' : '0')};
        padding-right: ${({position}) => ((position === 'left' || position === 'center') ? `max(100px, ${pxToVw(110, 'd')}` : '0')};
        width: calc(50% - max(70px, ${pxToVw(70, 'd')}));
    }
`;

const Headline = styled.h3`
    ${FONTS.LIGHT}
    color: ${({theme}) => theme.colors.textColor};
    font-size: ${clampCss(20, 'm')};
    letter-spacing: ${clampCss(1.2, 'm')};
    line-height: ${clampCss(24, 'm')};
    margin: 0;
    text-align: left;

    ${MEDIA.TABLET.UP} {
        font-size: ${clampCss(20, 't')};
        letter-spacing: ${clampCss(1.2, 't')};
        line-height: ${clampCss(24, 't')};
    }

    ${MEDIA.DESKTOP.UP} {
        font-size: max(20px, ${pxToVw(20, 'd')});
        letter-spacing: max(1.2px, ${pxToVw(1.2, 'd')});
        line-height: max(24px, ${pxToVw(24, 'd')});
    }
`;

const Text = styled.div`
    ${FONTS.REGULAR}
    color: ${({theme}) => theme.colors.textColor};
    font-size: ${clampCss(12, 'm')};
    letter-spacing: ${clampCss(0.5, 'm')};
    line-height: ${clampCss(16.8, 'm')};
    margin-top: ${clampCss(24, 'm')};
    text-align: left;

    ${MEDIA.TABLET.UP} {
        font-size: ${clampCss(12, 't')};
        letter-spacing: ${clampCss(0.5, 't')};
        line-height: ${clampCss(16.8, 't')};
        margin-top: ${clampCss(24, 't')};
    }

    ${MEDIA.DESKTOP.UP} {
        font-size: max(14px, ${pxToVw(14, 'd')});
        letter-spacing: max(0.5px, ${pxToVw(0.5, 'd')});
        line-height: max(19.6px, ${pxToVw(19.6, 'd')});
        margin-top: max(32px, ${pxToVw(32, 'd')});
    }
`;

const Download = styled.a`
    ${FONTS.SEMIBOLD}
    color: ${({theme}) => theme.colors.textColor};
    display: block;
    font-size: ${clampCss(12, 'm')};
    letter-spacing: ${clampCss(0.5, 'm')};
    line-height: ${clampCss(16.8, 'm')};
    margin-top: ${clampCss(40, 'm')};
    text-align: left;
    text-decoration: none;

    &::before {
        background-image: url(${iconAsBackground(DownloadIcon)});
        background-position: center center;
        background-repeat: no-repeat;
        background-size: contain;
        content: '';
        display: inline-block;
        height: ${clampCss(16, 'm')};
        margin-right: ${clampCss(8, 'm')};
        position: relative;
        top: ${clampCss(3, 'm')};
        width: ${clampCss(16, 'm')};
    }

    ${MEDIA.TABLET.UP} {
        font-size: ${clampCss(12, 't')};
        letter-spacing: ${clampCss(0.5, 't')};
        line-height: ${clampCss(16.8, 't')};
        margin-top: ${clampCss(32, 't')};

        &::before {
            height: ${clampCss(16, 't')};
            margin-right: ${clampCss(8, 't')};
            top: ${clampCss(3, 't')};
            width: ${clampCss(16, 't')};
        }
    }

    ${MEDIA.DESKTOP.UP} {
        font-size: max(14px, ${pxToVw(14, 'd')});
        letter-spacing: max(0.5px, ${pxToVw(0.5, 'd')});
        line-height: max(19.6px, ${pxToVw(19.6, 'd')});
        margin-top: max(32px, ${pxToVw(32, 'd')});

        &::before {
            height: max(16px, ${pxToVw(16, 'd')});
            margin-right: max(8px, ${pxToVw(8, 'd')});
            top: max(3px, ${pxToVw(3, 'd')});
            width: max(16px, ${pxToVw(16, 'd')});
        }
    }
`;